/**
 * Browser-side audio plumbing for live transcription: turns a MediaStream into
 * 16 kHz mono PCM16 chunks, and locates the remote (Chime) audio track so the
 * other side of the call can be transcribed with its own speaker label.
 */

export const TARGET_SAMPLE_RATE = 16000
const PCM_BUFFER_SIZE = 4096

export type PcmStreamer = { stop: () => void }

export function float32ToInt16(samples: Float32Array): ArrayBuffer {
  const out = new Int16Array(samples.length)
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]))
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff
  }
  return out.buffer
}

export function downsample(buffer: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate) return buffer
  const ratio = fromRate / toRate
  const length = Math.round(buffer.length / ratio)
  const result = new Float32Array(length)
  for (let i = 0; i < length; i++) {
    result[i] = buffer[Math.round(i * ratio)] ?? 0
  }
  return result
}

/** Streams `stream` as PCM16 @16 kHz through `onChunk` until `stop()` is called. */
export function createPcmStreamer(stream: MediaStream, onChunk: (chunk: ArrayBuffer) => void): PcmStreamer {
  const ctx = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE })
  const source = ctx.createMediaStreamSource(stream)
  const processor = ctx.createScriptProcessor(PCM_BUFFER_SIZE, 1, 1)
  processor.onaudioprocess = (event) => {
    const input = event.inputBuffer.getChannelData(0)
    const resampled = ctx.sampleRate !== TARGET_SAMPLE_RATE ? downsample(input, ctx.sampleRate, TARGET_SAMPLE_RATE) : input
    onChunk(float32ToInt16(resampled))
  }
  source.connect(processor)
  // A ScriptProcessorNode only runs when it is connected to the graph output. Keep it silent.
  const mute = ctx.createGain()
  mute.gain.value = 0
  processor.connect(mute)
  mute.connect(ctx.destination)

  return {
    stop: () => {
      processor.onaudioprocess = null
      processor.disconnect()
      source.disconnect()
      mute.disconnect()
      ctx.close().catch(() => undefined)
    },
  }
}

export async function captureMicrophone(): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: { channelCount: 1, sampleRate: { ideal: TARGET_SAMPLE_RATE }, echoCancellation: true, noiseSuppression: true },
  })
}

/**
 * The Chime component library binds the mixed remote audio to a hidden `<audio>` element
 * (`LocalAudioOutputProvider`). Its `srcObject` is the remote MediaStream: we clone its
 * audio track so transcription never interferes with playback.
 */
export function findRemoteAudioStream(root: ParentNode = document): MediaStream | null {
  const elements = Array.from(root.querySelectorAll('audio'))
  for (const element of elements) {
    const src = element.srcObject
    if (!(src instanceof MediaStream)) continue
    const tracks = src.getAudioTracks().filter((track) => track.readyState === 'live')
    if (!tracks.length) continue
    return new MediaStream(tracks.map((track) => track.clone()))
  }
  return null
}

export function stopStream(stream: MediaStream | null | undefined): void {
  stream?.getTracks().forEach((track) => track.stop())
}
